import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

interface Symptom {
  id: string;
  title: string;
  category: string;
  icon: string;
  slug: string;
  science_content: string;
  excerpt: string;
  is_published: boolean;
}

interface QuizQuestion {
  id?: string;
  question: string;
  options: any; // Json type from Supabase
  correct_index: number;
  explanation: string;
  order_index: number;
}

interface Tool {
  id?: string;
  title: string;
  description: string;
  icon: string;
  order_index: number;
}

interface Product {
  id?: string;
  name: string;
  description: string;
  link: string;
  image_url: string;
  order_index: number;
}

const CATEGORIES = [
  { value: 'physical', label: 'Physical' },
  { value: 'mental_emotional', label: 'Mental/Emotional' },
  { value: 'urogenital', label: 'Urogenital' },
  { value: 'sleep', label: 'Sleep' },
  { value: 'cognitive', label: 'Cognitive' },
  { value: 'cardiovascular', label: 'Cardiovascular' },
  { value: 'metabolic', label: 'Metabolic' },
];

export default function SymptomLibraryAdmin() {
  const [symptoms, setSymptoms] = useState<Symptom[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSymptom, setSelectedSymptom] = useState<Symptom | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    loadSymptoms();
  }, []);

  const loadSymptoms = async () => {
    try {
      const { data, error } = await supabase
        .from('symptom_library')
        .select('*')
        .order('title');

      if (error) throw error;
      setSymptoms(data || []);
    } catch (error: any) {
      toast.error('Failed to load symptoms');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadSymptomDetails = async (symptomId: string) => {
    try {
      const [quizRes, toolsRes, productsRes] = await Promise.all([
        supabase.from('symptom_quiz_questions').select('*').eq('symptom_id', symptomId).order('order_index'),
        supabase.from('symptom_tools').select('*').eq('symptom_id', symptomId).order('order_index'),
        supabase.from('symptom_products').select('*').eq('symptom_id', symptomId).order('order_index'),
      ]);

      if (quizRes.error) throw quizRes.error;
      if (toolsRes.error) throw toolsRes.error;
      if (productsRes.error) throw productsRes.error;

      setQuizQuestions(quizRes.data || []);
      setTools(toolsRes.data || []);
      setProducts(productsRes.data || []);
    } catch (error: any) {
      toast.error('Failed to load symptom details');
      console.error(error);
    }
  };

  const handleCreateOrUpdate = async (formData: FormData) => {
    const symptomData = {
      title: formData.get('title') as string,
      category: formData.get('category') as any,
      icon: formData.get('icon') as string,
      slug: (formData.get('title') as string).toLowerCase().replace(/\s+/g, '-'),
      science_content: formData.get('science_content') as string,
      excerpt: formData.get('excerpt') as string,
      is_published: formData.get('is_published') === 'true',
    };

    try {
      if (selectedSymptom?.id) {
        const { error } = await supabase
          .from('symptom_library')
          .update(symptomData)
          .eq('id', selectedSymptom.id);

        if (error) throw error;
        toast.success('Symptom updated successfully');
      } else {
        const { error } = await supabase
          .from('symptom_library')
          .insert([symptomData]);

        if (error) throw error;
        toast.success('Symptom created successfully');
      }

      setIsDialogOpen(false);
      setSelectedSymptom(null);
      loadSymptoms();
    } catch (error: any) {
      toast.error(`Failed to save symptom: ${error.message}`);
      console.error(error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this symptom? This will also delete all associated quiz questions, tools, and products.')) return;

    try {
      const { error } = await supabase
        .from('symptom_library')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Symptom deleted successfully');
      loadSymptoms();
    } catch (error: any) {
      toast.error('Failed to delete symptom');
      console.error(error);
    }
  };

  const handleEditSymptom = (symptom: Symptom) => {
    setSelectedSymptom(symptom);
    loadSymptomDetails(symptom.id);
    setIsDialogOpen(true);
  };

  if (loading) {
    return <div className="p-6">Loading symptoms...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Symptom Library Management</h2>
          <p className="text-muted-foreground">Manage symptoms, quiz questions, tools, and product recommendations</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setSelectedSymptom(null); setQuizQuestions([]); setTools([]); setProducts([]); }}>
              <Plus className="mr-2 h-4 w-4" />
              Add Symptom
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedSymptom ? 'Edit Symptom' : 'Create New Symptom'}</DialogTitle>
              <DialogDescription>
                Add comprehensive information about this menopause symptom
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleCreateOrUpdate(new FormData(e.currentTarget));
            }}>
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="quiz">Quiz</TabsTrigger>
                  <TabsTrigger value="tools">Tools</TabsTrigger>
                  <TabsTrigger value="products">Products</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      name="title"
                      defaultValue={selectedSymptom?.title}
                      required
                      placeholder="e.g., Hot Flashes"
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select name="category" defaultValue={selectedSymptom?.category || 'physical'} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="icon">Icon (Emoji)</Label>
                    <Input
                      id="icon"
                      name="icon"
                      defaultValue={selectedSymptom?.icon || '🔴'}
                      placeholder="🔥"
                    />
                  </div>

                  <div>
                    <Label htmlFor="excerpt">Excerpt (Brief Description)</Label>
                    <Textarea
                      id="excerpt"
                      name="excerpt"
                      defaultValue={selectedSymptom?.excerpt}
                      rows={2}
                      placeholder="A brief description..."
                    />
                  </div>

                  <div>
                    <Label htmlFor="science_content">Science Content (Detailed Explanation)</Label>
                    <Textarea
                      id="science_content"
                      name="science_content"
                      defaultValue={selectedSymptom?.science_content}
                      rows={10}
                      required
                      placeholder="Detailed scientific explanation of the symptom..."
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_published"
                      name="is_published"
                      defaultChecked={selectedSymptom?.is_published ?? true}
                    />
                    <Label htmlFor="is_published">Published</Label>
                  </div>
                </TabsContent>

                <TabsContent value="quiz" className="space-y-4 mt-4">
                  <p className="text-sm text-muted-foreground">
                    Quiz questions will be managed after creating the symptom
                  </p>
                </TabsContent>

                <TabsContent value="tools" className="space-y-4 mt-4">
                  <p className="text-sm text-muted-foreground">
                    Tools will be managed after creating the symptom
                  </p>
                </TabsContent>

                <TabsContent value="products" className="space-y-4 mt-4">
                  <p className="text-sm text-muted-foreground">
                    Products will be managed after creating the symptom
                  </p>
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-2 mt-6">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  <Save className="mr-2 h-4 w-4" />
                  Save Symptom
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {symptoms.map((symptom) => (
          <Card key={symptom.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{symptom.icon}</span>
                  <div>
                    <CardTitle className="text-lg">{symptom.title}</CardTitle>
                    <Badge variant="secondary" className="mt-1">
                      {CATEGORIES.find(c => c.value === symptom.category)?.label}
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="icon" variant="ghost" onClick={() => handleEditSymptom(symptom)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(symptom.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription className="line-clamp-2">
                {symptom.excerpt || symptom.science_content.substring(0, 100) + '...'}
              </CardDescription>
              <div className="mt-2">
                <Badge variant={symptom.is_published ? "default" : "secondary"}>
                  {symptom.is_published ? 'Published' : 'Draft'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {symptoms.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">No symptoms yet. Create your first one!</p>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add First Symptom
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}